import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ChevronLeft, ChevronRight, X, Calendar, MapPin, Images, Video, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Fallback static data for GASI 2026 Bangkok (in case API returns empty)
const staticEventsData = [
  {
    id: 'gasi-2026-static',
    title: "GASI 2026 Bangkok",
    date: "2026-02-12",
    location: "Bangkok, Thailand",
    description: "Global Aesthetic Safety Initiative - International Congress on Complication Management in Aesthetic Medicine",
    status: "active",
    media: [
      { id: '1', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/xgflf6le_WhatsApp%20Image%202026-02-26%20at%205.45.30%20PM.jpeg" },
      { id: '2', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/61sqwj2y_IMG_20260213_150957.jpg" },
      { id: '3', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/5ur87max_IMG_20260215_165602.jpg" },
      { id: '4', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/obddgasi_DSC_2698.jpg" },
      { id: '5', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/0pi5om6w_DSC_1068.JPG" },
      { id: '6', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/7ax30oxi_DSC_2962.JPG" },
      { id: '7', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/ihs0ag73_DSC_2944.jpg" },
      { id: '8', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/9r2jy4oi_DSC_1261.JPG" },
      { id: '9', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/r9y0pze9_DSC_1389.jpg" },
      { id: '10', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/9uh8s4ns_DSC_2007.jpg" },
      { id: '11', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/sb307fle_DSC_2405.JPG" },
      { id: '12', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/ui9cyllo_DSC_1840.jpg" },
      { id: '13', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/xkwon9x8_DSC_2486.jpg" },
      { id: '14', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/d66cd2th_DSC_2388.jpg" },
      { id: '15', type: 'image', url: "https://customer-assets.emergentagent.com/job_32d6feb3-5f9e-4ec6-ac88-bba0f5d97d0d/artifacts/3dv6p1wt_DSC_1416.jpg" },
    ]
  }
];

export default function EventsGalleryPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxEvent, setLightboxEvent] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxType, setLightboxType] = useState('image'); // 'image' or 'video'

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/api/events-gallery/public`);
      const apiEvents = res.data.events || [];
      // Combine API events with static fallback if API is empty
      if (apiEvents.length > 0) {
        setEvents(apiEvents);
      } else {
        setEvents(staticEventsData);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
      setEvents(staticEventsData);
    } finally {
      setLoading(false);
    }
  };

  const getImages = (event) => event.media?.filter(m => m.type === 'image') || [];
  const getVideos = (event) => event.media?.filter(m => m.type === 'video') || [];

  const openLightbox = (event, index, type = 'image') => {
    setLightboxEvent(event);
    setLightboxIndex(index);
    setLightboxType(type);
  };

  const closeLightbox = () => {
    setLightboxEvent(null);
    setLightboxIndex(0);
    setLightboxType('image');
  };

  const getCurrentMedia = () => {
    if (!lightboxEvent) return [];
    return lightboxType === 'image' ? getImages(lightboxEvent) : getVideos(lightboxEvent);
  };

  const nextMedia = () => {
    const media = getCurrentMedia();
    setLightboxIndex((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  };

  const prevMedia = () => {
    const media = getCurrentMedia();
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="events-gallery-page">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white border-b border-slate-100">
        <div className="imcac-container text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#1B2A4A]/5 rounded-sm mb-6">
            <Images className="h-4 w-4 text-[#C5A572]" />
            <span className="text-xs font-semibold tracking-wider text-[#1B2A4A] uppercase">IMCAC Events</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#1B2A4A] font-heading mb-4">
            Events Gallery
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Explore moments from our international congresses, examinations, and professional events.
          </p>
        </div>
      </section>

      {/* Events Section */}
      <section className="py-12">
        <div className="imcac-container">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-[#C5A572]" />
              <span className="ml-3 text-slate-500">Loading events...</span>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-20">
              <Images className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#1B2A4A] mb-2">No Events Yet</h3>
              <p className="text-slate-500">Check back soon for upcoming event galleries.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {events.map((event, eventIndex) => {
                const images = getImages(event);
                const videos = getVideos(event);
                
                return (
                  <Card key={event.id} className="overflow-hidden border-slate-200" data-testid={`event-card-${eventIndex}`}>
                    <CardContent className="p-0">
                      {/* Event Header */}
                      <div className="p-6 bg-[#1B2A4A] text-white">
                        <h3 className="text-2xl font-bold font-heading mb-2">{event.title}</h3>
                        <div className="flex flex-wrap gap-4 text-sm text-slate-300">
                          {event.date && (
                            <span className="flex items-center gap-2">
                              <Calendar className="h-4 w-4 text-[#C5A572]" />
                              {formatDate(event.date)}
                            </span>
                          )}
                          {event.location && (
                            <span className="flex items-center gap-2">
                              <MapPin className="h-4 w-4 text-[#C5A572]" />
                              {event.location}
                            </span>
                          )}
                          {images.length > 0 && (
                            <span className="flex items-center gap-2">
                              <Images className="h-4 w-4 text-[#C5A572]" />
                              {images.length} Photos
                            </span>
                          )}
                          {videos.length > 0 && (
                            <span className="flex items-center gap-2">
                              <Video className="h-4 w-4 text-[#C5A572]" />
                              {videos.length} Videos
                            </span>
                          )}
                        </div>
                        {event.description && (
                          <p className="mt-3 text-slate-300">{event.description}</p>
                        )}
                      </div>
                      
                      {/* Image Gallery Grid */}
                      {images.length > 0 && (
                        <div className="p-6">
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {images.map((media, index) => (
                              <div 
                                key={media.id || index}
                                className="aspect-square overflow-hidden rounded-lg cursor-pointer group relative"
                                onClick={() => openLightbox(event, index, 'image')}
                              >
                                <img 
                                  src={media.url} 
                                  alt={`${event.title} - Photo ${index + 1}`}
                                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                                  <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity text-sm font-medium">
                                    View
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Video Gallery Grid */}
                      {videos.length > 0 && (
                        <div className="p-6 pt-0">
                          <h4 className="text-sm font-semibold text-[#1B2A4A] mb-4 flex items-center gap-2">
                            <Video className="h-4 w-4 text-[#C5A572]" /> Videos
                          </h4>
                          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {videos.map((media, index) => (
                              <div 
                                key={media.id || index}
                                className="relative aspect-video overflow-hidden rounded-lg cursor-pointer group bg-black"
                                onClick={() => openLightbox(event, index, 'video')}
                              >
                                <video 
                                  src={media.url}
                                  className="w-full h-full object-cover"
                                  muted
                                />
                                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/60 transition-all duration-300 flex items-center justify-center">
                                  <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                                    <Video className="h-8 w-8 text-white" />
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      <Dialog open={!!lightboxEvent} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-6xl w-full h-[90vh] p-0 bg-black border-none">
          <div className="relative w-full h-full flex items-center justify-center">
            {/* Close Button */}
            <button 
              onClick={closeLightbox}
              className="absolute top-4 right-4 z-50 p-2 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>

            {/* Navigation Buttons */}
            {getCurrentMedia().length > 1 && (
              <>
                <button 
                  onClick={prevMedia}
                  className="absolute left-4 z-50 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronLeft className="h-8 w-8" />
                </button>
                <button 
                  onClick={nextMedia}
                  className="absolute right-4 z-50 p-3 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
                >
                  <ChevronRight className="h-8 w-8" />
                </button>
              </>
            )}

            {/* Media Content */}
            {lightboxEvent && (
              lightboxType === 'image' ? (
                <img 
                  src={getCurrentMedia()[lightboxIndex]?.url} 
                  alt={`${lightboxEvent.title} - Photo ${lightboxIndex + 1}`}
                  className="max-w-full max-h-full object-contain"
                />
              ) : (
                <video 
                  src={getCurrentMedia()[lightboxIndex]?.url}
                  className="max-w-full max-h-full"
                  controls
                  autoPlay
                />
              )
            )}

            {/* Media Counter */}
            {lightboxEvent && getCurrentMedia().length > 1 && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-black/50 rounded-full text-white text-sm">
                {lightboxIndex + 1} / {getCurrentMedia().length}
              </div>
            )}

            {/* Event Info */}
            {lightboxEvent && (
              <div className="absolute bottom-4 left-4 px-4 py-2 bg-black/50 rounded-lg text-white">
                <p className="font-semibold">{lightboxEvent.title}</p>
                <p className="text-sm text-slate-300">{formatDate(lightboxEvent.date)}</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
